export type ClaudePromptSurface = 'homepage' | 'dashboard' | 'chart_group';

export type ClaudePromptTarget = {
  surface: ClaudePromptSurface;
  dashboardId: string;
  dashboardName: string;
  objectId?: string;
  objectName?: string;
};

const CLI_PACKAGE = '@kizenapps/cli@latest';
const CLI = `npx -y ${CLI_PACKAGE}`;

const BLOCK_TYPES: Record<ClaudePromptSurface, string> = {
  homepage: 'homepages',
  dashboard: 'dashboards',
  chart_group: 'charts',
};

const SURFACE_LABELS: Record<ClaudePromptSurface, string> = {
  homepage: 'homepage',
  dashboard: 'dashboard',
  chart_group: 'chart group',
};

export const buildClaudePrompt = (target: ClaudePromptTarget, host: string) => {
  const surface = SURFACE_LABELS[target.surface];
  const { dashboardId } = target;
  const blockType = BLOCK_TYPES[target.surface];

  const intro = `Build a new custom block and publish it to the Kizen ${surface} "${target.dashboardName}" (id ${dashboardId}) on ${host}. Don't assume anything is set up; follow these steps in order.`;

  const chartGroupNote =
    target.surface === 'chart_group'
      ? `It's a chart group for the ${target.objectName || target.objectId} object (id ${target.objectId}), so the block receives this.args.objectId.`
      : null;

  const steps = [
    '1. Run `node --version`. If Node.js is missing or older than v20, stop and tell me to install Node.js 20 or newer.',
    `2. Run the Kizen CLI as \`${CLI} <args>\` every time; the \`@latest\` tag is required. Wherever the skill or CLI output says \`appbuilder <args>\`, run \`${CLI} <args>\` instead.`,
    `3. If the current directory has no \`kizen.json\`, create a plugin: make a new empty folder (for example \`kizen-blocks\`), cd into it, and run \`${CLI} create --name "Kizen Blocks" --artifacts block\`. It scaffolds into the current directory; its warning about a missing developer_business_id is expected. Run every later command from the plugin folder.`,
    `4. Run \`${CLI} setup-claude\`, then read \`.claude/skills/kizen-custom-block/SKILL.md\` and \`.claude/skills/kizen-custom-block/design.md\` from disk and follow them. A skill installed during this session is not loaded automatically.`,
    `5. Check credentials with \`${CLI} block targets --json\`. If it returns \`credentials_invalid\`, there are no usable credentials and you can't drive the interactive setup: ask me to run \`npx ${CLI_PACKAGE} dev\` in my own terminal from the plugin folder (give me its full path), choose **Global** credential mode (Local keeps credentials in the browser, where \`block\` commands can't read them), enter my API key, user ID, business ID and the environment ${host} belongs to (go, fmo, staging, integration or test1). If \`dev\` opens without asking for credentials, tell me to press \`c\` there to set them. Once I quit with \`q\` and tell you to continue, check again. If it returns \`needs_choice\` with \`"choice": "profile"\`, ask me which profile to use and pass \`--profile <name>\` to every \`block\` command.`,
    `6. Ask me what the block should show, then build and validate it following the skill. Its \`config.json\` \`types\` must include \`${blockType}\`, or it won't show up on this ${surface}. The target above is already chosen, so skip the skill's target-picking step. Show me a dry run with \`${CLI} block push <api_name> --dashboard ${dashboardId} --create --dry-run --json\`.`,
    `7. When I approve, push it with \`${CLI} block push <api_name> --dashboard ${dashboardId} --create --yes --json\` and refresh my open Kizen tab as the skill describes.`,
  ];

  return [
    chartGroupNote ? `${intro} ${chartGroupNote}` : intro,
    '',
    ...steps,
  ].join('\n');
};
